import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { useUserContext } from "../../contexts/UserContext";

export default function MiscellaneousDebitModal({
  open, closeModal, orderNumber, ledgerBalance, handleRefreshList, handleRefreshBalance
}: {
  open: boolean;
  closeModal: () => void;
  orderNumber: string;
  ledgerBalance: number | null;
  handleRefreshList: () => Promise<void>;
  handleRefreshBalance: () => Promise<void>;
}) {
  const user = useUserContext()?.user;
  const [amount, setAmount] = useState("");
  const [payeeName, setPayeeName] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [postedDate, setPostedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [memo, setMemo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState("");
  const submissionInProgress = useRef(false);
  const createdBy = user?.name || user?.username;
  const validAmount = /^\d+(\.\d{1,2})?$/.test(amount) && Number(amount) > 0 && Number.isFinite(Number(amount));
  const insufficientFunds = validAmount && ledgerBalance !== null && Number(amount) > ledgerBalance;
  const trimmedReference = referenceNumber.trim();
  const validReference = !trimmedReference || (/^\d+$/.test(trimmedReference) &&
    Number(trimmedReference) > 0 && Number(trimmedReference) <= 999999999999);
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(postedDate) && dayjs(postedDate).isValid() && dayjs(postedDate).format("YYYY-MM-DD") === postedDate;
  const canSubmit = validAmount && !insufficientFunds && ledgerBalance !== null && payeeName.trim() &&
    validReference && validDate && createdBy && !submitting && !saved && !uncertain;

  const handleSubmit = async () => {
    if (!canSubmit || submissionInProgress.current) return;
    submissionInProgress.current = true;
    setSubmitting(true);
    setError("");
    try {
      await axios.post("/proxy/api/ledgers/CreateMiscellaneousDebit", {
        orderNumber, amount, payeeName: payeeName.trim(), referenceNumber: trimmedReference,
        postedDate, memo: memo.trim(), createdBy
      });
    } catch (failure) {
      const validationFailure = axios.isAxiosError(failure) && failure.response?.status === 400;
      if (validationFailure) {
        const data = failure.response?.data;
        setError(data?.message || data?.Message || "Check the values and try again.");
      } else {
        setUncertain(true);
        setError("The posting result could not be confirmed. Close this form and reload the ledger to check for this debit before trying again.");
      }
      setSubmitting(false);
      submissionInProgress.current = false;
      return;
    }

    setSaved(true);
    try {
      await Promise.all([handleRefreshList(), handleRefreshBalance()]);
      closeModal();
    } catch {
      setError("The debit was posted, but the ledger could not refresh. Close this form and reload the ledger.");
    } finally {
      setSubmitting(false);
      submissionInProgress.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : closeModal} fullWidth maxWidth="sm">
      <DialogTitle>New Miscellaneous Debit</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            Record a bank-issued check or other miscellaneous debit. This posts the debit immediately and reduces the ledger balance.
          </Typography>
          {error && <Alert severity={saved ? "warning" : "error"}>{error}</Alert>}
          <TextField label="Amount" value={amount} onChange={(event) => setAmount(event.target.value)}
            inputProps={{ inputMode: "decimal" }} required disabled={submitting || saved || uncertain}
            error={!!amount && (!validAmount || insufficientFunds)}
            helperText={insufficientFunds ? "There are insufficient funds for this transaction." : "Enter a positive amount with up to two decimal places."} />
          <TextField label="Payee" value={payeeName} onChange={(event) => setPayeeName(event.target.value)}
            required disabled={submitting || saved || uncertain} />
          <TextField label="Bank check / reference number" value={referenceNumber} onChange={(event) => setReferenceNumber(event.target.value)}
            inputProps={{ inputMode: "numeric" }} disabled={submitting || saved || uncertain}
            error={!validReference} helperText={!validReference ? "Enter a positive whole number up to 999999999999." : "Optional"} />
          <TextField label="Posting date" type="date" value={postedDate} onChange={(event) => setPostedDate(event.target.value)}
            InputLabelProps={{ shrink: true }} required error={!validDate} disabled={submitting || saved || uncertain} />
          <TextField label="Memo" value={memo} onChange={(event) => setMemo(event.target.value)}
            multiline minRows={2} disabled={submitting || saved || uncertain} />
          {ledgerBalance === null && <Alert severity="info">Wait for the ledger balance to load before posting.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={closeModal} disabled={submitting}>{saved || uncertain ? "Close" : "Cancel"}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>{submitting ? "Posting…" : "Post Debit"}</Button>
      </DialogActions>
    </Dialog>
  );
}
